import type { FieldValue } from '../../../data/client';
import type { ToastInput } from '../../../ui';
import { type MoveFeedbackContext, type MoveWriter, moveFeedback } from '../kanban/applyMove';

/**
 * DRAGGING A RECORD INTO ANOTHER GROUP WRITES THE FIELD
 * (`docs/decisions/group-by-aggregation.md` D24).
 *
 * It is the board's rule on the table, and it is universal in the research: Airtable states
 * it outright and states the one exception that matters - "You cannot add new records to a
 * grouping that is based on a computed field type". Ours is `groupDropValue` refusing a
 * DATE or DATETIME bucket, one layer up, so a bucketed dimension registers no drop targets
 * at all rather than accepting a drop and quietly ignoring it.
 *
 * THE READING OF THE THREE OUTCOMES IS THE BOARD'S, IMPORTED (INV-10 pending, INV-5
 * blocked, applied). What is NOT shared is the write itself: a board column always writes
 * an option id, while a table group writes whatever its dimension holds - an option id, a
 * boolean, a target record id, or null for the no-value group, which is exactly what the
 * board's own empty bucket already writes.
 *
 * TWENTY'S "BLOCKED WHILE A SORT IS ACTIVE" MODAL IS NOT ADOPTED, and the reason is a
 * property of our table rather than a disagreement. Their drop also writes a manual
 * position, so a sort makes the result unpredictable; ours writes the group field and
 * nothing else, and our table ALWAYS has a sort - `defaultOrderBy` resolves `position asc`
 * when the caller supplies none - so their rule would block every drop we can offer. The
 * row is written, the sort re-places it, and if it moves that is the sort being true.
 */
export interface GroupDropInput extends MoveFeedbackContext {
  readonly recordId: string;
  /** What the destination group's key says this field should now hold (D24). */
  readonly value: FieldValue;
}

export interface ApplyGroupDropArgs {
  readonly input: GroupDropInput;
  readonly write: MoveWriter;
  readonly toast: (input: ToastInput) => void;
}

export function applyGroupDrop({ input, write, toast }: ApplyGroupDropArgs): void {
  write(
    { recordId: input.recordId, values: { [input.groupField.id]: input.value } },
    {
      onSuccess: (result) => {
        const feedback = moveFeedback(input, result);
        if (feedback.toast !== null) toast(feedback.toast);
      },
      onError: (error) => {
        // The hook has already rolled the optimistic value back, so the row is home; all
        // that is missing is saying so.
        toast({
          title: 'Could not move the record',
          description: error.message,
          tone: 'danger',
        });
      },
    },
  );
}

/** The dnd-kit id of a draggable row, and its inverse. Prefixed, so nothing collides. */
export function rowDragId(recordId: string): string {
  return `grouprow:${recordId}`;
}

export function recordIdOfRowDragId(id: string | number): string | null {
  const text = String(id);
  return text.startsWith('grouprow:') ? text.slice('grouprow:'.length) : null;
}

/** The dnd-kit id of a group's drop target, built from the ONE group-key encoding (D5). */
export function groupDropId(encodedKey: string): string {
  return `group:${encodedKey}`;
}

export function encodedKeyOfDropId(id: string | number | undefined): string | null {
  if (id === undefined) return null;
  const text = String(id);
  return text.startsWith('group:') ? text.slice('group:'.length) : null;
}
